/** @jsx jsx */
import { jsx } from '@emotion/react';

import { useEffect, useRef } from 'react';

import { isEqual } from 'lodash';

import BaseSelectQuestion from 'bundles/onboarding-common/components/modular/BaseSelectQuestion';
import { QUESTION_IDS } from 'bundles/onboarding-common/constants/questionScheme';
import { useOnboardingOptions } from 'bundles/onboarding-common/contexts/OnboardingOptionsContext';
import { useOnboardingQuestionData } from 'bundles/onboarding-common/contexts/OnboardingQuestionDataContext';
import useFetchData from 'bundles/onboarding-common/hooks/useFetchData';
import useHasIndicatedUnsureOfRoleOrSkills from 'bundles/onboarding-common/hooks/useHasIndicatedUnsureOfRoleOrSkills';
import useSkillSelectQuestionState from 'bundles/onboarding-common/hooks/useSkillSelectQuestionState';
import useSkillsWantedData from 'bundles/onboarding-common/hooks/useSkillsWantedData';
import type { Skill } from 'bundles/onboarding-common/types';
import { getIdontKnowOption } from 'bundles/onboarding-common/utils';

import _t from 'i18n!nls/onboarding-common';

const SkillSelectQuestion = ({
  skills,
  skillsWantedData,
  defaultSingleSelectOptionIsSelected,
}: {
  skills: Skill[];
  skillsWantedData: Skill[] | null;
  defaultSingleSelectOptionIsSelected: boolean;
}) => {
  const {
    skillsPerRow,
    containerRef,
    handleClick,
    onViewMoreClick,
    onViewAllClick,
    isLoading,
    isExpanded,
    shouldUseDefaultLayout,
    hasShownAllItems,
  } = useSkillSelectQuestionState({ skills, skillsWantedData, defaultSingleSelectOptionIsSelected });

  return (
    <BaseSelectQuestion
      itemsPerRow={skillsPerRow}
      containerRef={containerRef}
      handleClick={handleClick}
      onViewMoreClick={onViewMoreClick}
      onViewAllClick={onViewAllClick}
      isLoading={isLoading}
      isExpanded={isExpanded}
      shouldUseDefaultLayout={shouldUseDefaultLayout}
      getLabel={(item) => item.skillName || ''}
      ariaLabel={_t('Select your skill(s)')}
      hasShownAllItems={hasShownAllItems}
    />
  );
};

const SkillSelectQuestionWrapper = () => {
  const { setQuestionValue } = useOnboardingQuestionData();
  const { allSkills, isLoadingSkills } = useOnboardingOptions();
  const { value: skillsWantedData, loading } = useFetchData({
    useQueryData: useSkillsWantedData,
    questionIdForLocalStorage: QUESTION_IDS.skillSelect,
  });
  const { hasIndicatedUnsureOfSkill, loading: isLoadingIsUnsureOfSkills } = useHasIndicatedUnsureOfRoleOrSkills();

  const previousSkillsWantedData = useRef<Skill[] | null>(null);

  useEffect(() => {
    if (skillsWantedData && !isEqual(skillsWantedData, previousSkillsWantedData.current)) {
      previousSkillsWantedData.current = skillsWantedData;

      if (skillsWantedData.length > 0) {
        setQuestionValue({
          __typename: QUESTION_IDS.skillSelect,
          value: skillsWantedData,
        });
      } else if (hasIndicatedUnsureOfSkill) {
        const { id, name, type } = getIdontKnowOption();
        setQuestionValue({
          __typename: QUESTION_IDS.skillSelect,
          value: [{ id, type, skillName: name }],
        });
      }
    }
  }, [skillsWantedData, hasIndicatedUnsureOfSkill, setQuestionValue]);

  if (isLoadingSkills || loading || isLoadingIsUnsureOfSkills) {
    // TODO: Add a loading state
    return null;
  }

  return (
    <SkillSelectQuestion
      skills={allSkills}
      skillsWantedData={skillsWantedData}
      defaultSingleSelectOptionIsSelected={!skillsWantedData?.length && hasIndicatedUnsureOfSkill}
    />
  );
};

export default SkillSelectQuestionWrapper;
